import { useToast } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import { createContext, use, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  useActionData,
  useFetcher,
  useLoaderData,
  useNavigation,
} from "react-router";

import { ActionPlan, ScenariosAndTrajectory } from "@/components/Trajectory";
import { UserContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import {
  TScenarioProjections,
  TScenarioProjectionsResponse,
  TTrajectoryAction,
} from "@/types";

export async function loader() {
  const actionsResponse = await fetch("/api/client/trajectory_actions");

  if (actionsResponse.status !== 200) {
    throw data("Failed to fetch the trajectory category actions", {
      status: actionsResponse.status,
    });
  }

  const { data: trajectoryActions } = (await actionsResponse.json()) as {
    data: Array<TTrajectoryAction>;
  };

  const actions = trajectoryActions
    .filter((trajectoryAction) => trajectoryAction.status === "valid")
    .sort((a, b) => b.reduction_factor - a.reduction_factor); // Sort by reduction factor;

  const availableActions = trajectoryActions
    .filter((trajectoryAction) => trajectoryAction.status === "available")
    .sort((a, b) => b.reduction_factor - a.reduction_factor); // Sort by reduction factor

  // Put recommended actions first
  const recommendedActions = availableActions.filter(
    (availableAction) => availableAction.recommended
  );
  const notRecommendedActions = availableActions.filter(
    (availableAction) => !availableAction.recommended
  );

  // Fetch the trajectory data
  const scenarioProjectionsResponse = await fetch(
    "/api/client/trajectories/scenario_projections"
  );

  if (scenarioProjectionsResponse.status !== 200) {
    throw data("Failed to fetch the trajectory scenario projections", {
      status: scenarioProjectionsResponse.status,
    });
  }

  const { data: scenarioProjectionsData } =
    (await scenarioProjectionsResponse.json()) as {
      data: TScenarioProjectionsResponse;
    };

  return {
    actions,
    availableActions: [...recommendedActions, ...notRecommendedActions],
    scenarioProjections: scenarioProjectionsData.scenarios_tco2e,
    averageYearlyTrajectoryReductionPercentage:
      scenarioProjectionsData.average_yearly_trajectory_reduction_percentage,
    reducedTrajectoryPercentage:
      scenarioProjectionsData.reduced_trajectory_percentage,
    reducedSbtiPercentage: scenarioProjectionsData.reduced_sbti_percentage,
  };
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  // _intent is used to differentiate between multiple action types in the same component
  // updateTrajectoryAction -> add/remove/update an action in the action plan
  // validateTrajectory -> validate the trajectory after the full action plan is confirmed
  // From: https://www.reddit.com/r/reactjs/comments/1lc4tq9/comment/mxxq47h/
  const intent = formData.get("_intent");

  if (intent === "updateTrajectoryAction") {
    const actionId = formData.get("actionId");
    const action = formData.get("action") as "add" | "remove" | null;
    const intensityPercentage = formData.get("intensityPercentage") as string;
    const difficulty = formData.get("difficulty") as string;
    // "valid"      -> currently added to the action plan
    // "available"  -> could be added to the action

    const body = {} as Record<string, string>;

    if (intensityPercentage) {
      body["intensity_percentage"] = intensityPercentage;
    }

    if (difficulty) {
      body["difficulty"] = difficulty;
    }

    if (action) {
      body["status"] = action === "add" ? "valid" : "available";
    }

    const response = await fetch(
      `/api/client/trajectory_actions/${actionId}/`,
      {
        method: request.method,
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.status !== 200) {
      throw data("Failed to update a trajectory action.", {
        status: response.status,
      });
    }

    return {
      intensityPercentageUpdated: Boolean(intensityPercentage),
    };
  } else if (intent === "validateTrajectory") {
    const isModification = formData.get("isModification");
    const response = await fetch("/api/client/trajectories/validate/", {
      method: request.method,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        is_modification: isModification,
      }),
    });

    if (response.status !== 204) {
      throw data("Failed to validate trajectory.", {
        status: response.status,
      });
    }

    return;
  }
}

export type TTrajectoryActionPlanContext = {
  scenarioProjections?: TScenarioProjections;
  averageYearlyTrajectoryReductionPercentage?: number;
  reducedTrajectoryPercentage?: number;
  reducedSbtiPercentage?: number;
};

export const TrajectoryActionPlanContext =
  createContext<TTrajectoryActionPlanContext>({});

function TrajectoryActionPlan() {
  const {
    actions,
    availableActions,
    scenarioProjections,
    averageYearlyTrajectoryReductionPercentage,
    reducedSbtiPercentage,
    reducedTrajectoryPercentage,
  } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const { user } = use(UserContext);
  const { t } = useTranslation("trajectory");
  const { toast } = useToast();
  const { sendSegmentEvent } = useSegmentEvent();
  const fetcher = useFetcher();

  // Show the trajectory if the action plan already has actions, otherwise show the action plan edit mode
  const [showTrajectory, setShowTrajectory] = useState(Boolean(actions.length));
  // Keeping the initial value to know if it's an action plan's first validation or a modification of an existing one
  const isModification = useRef(Boolean(actions.length));

  useEffect(() => {
    if (navigation.state === "idle" && actionData?.intensityPercentageUpdated) {
      toast({
        title: t("trajectory:trajectory.toast.actionAmbitionUpdated"),
        intent: "positive",
      });
    }
  }, [navigation.state, actionData]);

  return (
    <TrajectoryActionPlanContext
      value={{
        scenarioProjections,
        reducedTrajectoryPercentage,
        reducedSbtiPercentage,
      }}
    >
      <div
        className={cn(
          "gap-sm flex flex-col xl:grid xl:grid-cols-2",
          !showTrajectory && "flex-col-reverse"
        )}
      >
        {showTrajectory && (
          <ScenariosAndTrajectory
            scenarioProjections={scenarioProjections}
            averageYearlyTrajectoryReductionPercentage={
              averageYearlyTrajectoryReductionPercentage
            }
            reducedTrajectoryPercentage={reducedTrajectoryPercentage}
            reducedSbtiPercentage={reducedSbtiPercentage}
          />
        )}
        <ActionPlan
          isInEdition={!showTrajectory}
          onEditionChange={() => {
            const newShowTrajectory = !showTrajectory;
            setShowTrajectory(newShowTrajectory);

            if (newShowTrajectory) {
              fetcher.submit(
                {
                  _intent: "validateTrajectory",
                  isModification: isModification.current,
                },
                { method: "POST" }
              );
            }
          }}
          actions={actions}
          availableActions={availableActions}
        />
      </div>
    </TrajectoryActionPlanContext>
  );
}

export default TrajectoryActionPlan;
